import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { AppConfigService } from '../../../config/app-config.service';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { EmployeeNumbers } from '../../models/company_registration/employee-numbers';
import { LegalType } from '../../models/company_registration/legal-type';
import { Router } from '@angular/router';
import { LoginPermissionsDto } from '../../models/user-management/auth.models';
import { AuthzService } from '../company-admin/authz.service';
import { Observable } from 'rxjs';
import { ApiResponse } from '../company-admin/company-admin.service';
import { isPlatformBrowser } from '@angular/common';
import { NavigationTrackerService } from '../navigation-guard/navigation-tracker.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  //baseUrl: string;
  gatewayBaseUrl: string;
  //externamBaseUrl: string;

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private appConfigService: AppConfigService,
    private http: HttpClient,
    private router: Router,
    private authz: AuthzService,
    private navigationTracker: NavigationTrackerService
  ) {
    // this.baseUrl = this.appConfigService.getApiBaseUrl();
    this.gatewayBaseUrl = this.appConfigService.getApiGatewayBaseUrl();
    // this.externamBaseUrl = this.appConfigService.getApiExternalBaseUrl();

  }

  // Register company
  registerCompany(data: any): Observable<any> {
    //console.log('Registering company with data:', data);
    return this.http.post(
      `${this.gatewayBaseUrl}/api/EmployerAuth/RegisterCompany`,
      data
    );
  }

  // Validate Business Registration Number
  validateBrn(businessRegistrationNo: string): Observable<any> {
    return this.http.get(
      `${this.gatewayBaseUrl}/api/EmployerAuth/validate-brn`,
      { params: { businessRegistrationNo } }
    );
  }

  // login company
  login(data: any) {
    return this.http.post(
      `${this.gatewayBaseUrl}/api/CompanyAdmins/Login`,
      data
    );
  }

  // Send OTP
  verifyOtp(data: any) {
    return this.http.post(
      `${this.gatewayBaseUrl}/api/CompanyAdmins/VerifyLoginOtp`,
      data
    );
  }

  // Get company details by email
  getCompanyIdByEmail(email: string) {
    const headers = { 'Content-Type': 'application/json' };
    return this.http.post(
      `${this.gatewayBaseUrl}/Employee/GetCompanyIdByEmail`,
      JSON.stringify(email),
      { headers }
    );
  }

  //Get employee categories (dropdown values)
  getEmployeeCategoryTypes() {
    return this.http.get<{
      success: boolean;
      message: string;
      data: EmployeeNumbers[];
    }>(`${this.gatewayBaseUrl}/Company/GetEmployeeCategoryTypes`);
  }

  //Get legal type(dropdown values)
  getAllLegalTypes() {
    return this.http.get<{
      success: boolean;
      message: string;
      data: LegalType[];
    }>(`${this.gatewayBaseUrl}/Company/GetAllLegalTypes`);
  }

  //Get monthly salary bill values (dropdown values)
  getMonthlySalaryBillTypes() {
    return this.http.get<{
      success: boolean;
      message: string;
      data: LegalType[];
    }>(`${this.gatewayBaseUrl}/Company/GetMonthlySalaryBillTypes`);
  }

  // Store token in sessionStorage
  storeToken(token: string): void {
    sessionStorage.setItem('token', token);
  }

  // Retrieve token
  getToken(): string | null {
    if (isPlatformBrowser(this.platformId)) {
      return sessionStorage.getItem('token');
    }
    return null;
  }
  // Check if user is logged in
  isLoggedIn(): boolean {
    return !!this.getToken();
  }

  // Clear token on logout
  clearToken(): void {
    sessionStorage.removeItem('token');
  }

  // Store companyId in sessionStorage
  storeCompanyId(companyId: string): void {
    sessionStorage.setItem('companyId', companyId);
    //console.log('Company ID stored in session storage hi:', companyId);
  }

  // Retrieve companyId
  getCompanyId(): string | null {
    return sessionStorage.getItem('companyId');
  }

  // Clear companyId
  clearCompanyId(): void {
    sessionStorage.removeItem('companyId');
  }

  // Store company details in sessionStorage
  storeCompanyDetails(data: any): void {
    sessionStorage.setItem('companyName', data.companyName || '');
    sessionStorage.setItem('firstName', data.firstName || '');
    sessionStorage.setItem('lastName', data.lastName || '');
    sessionStorage.setItem('email', data.email || '');
    sessionStorage.setItem('lastLoginDate', data.lastLoginDate || '');
    sessionStorage.setItem('companyAdminId', data.companyAdminId || '');

  }

  // Store employerTypeId in sessionStorage
  storeEmployerTypeId(employerTypeId: number): void {
    sessionStorage.setItem('employerTypeId', employerTypeId.toString());
  }

  // Store employerEPFNo in sessionStorage
  storeEmployerEPFNo(employerEPFNo: string): void {
    if (employerEPFNo) {
      sessionStorage.setItem('companyEpfNumber', employerEPFNo);
    } else {
      // Clear if null or empty to avoid stale values
      this.clearEmployerEPFNo();
    }
  }

  // Retrieve employerEPFNo
  getEmployerEPFNo(): string | null {
    return sessionStorage.getItem('companyEpfNumber');
  }

  // Clear employerEPFNo
  clearEmployerEPFNo(): void {
    sessionStorage.removeItem('companyEpfNumber');
    sessionStorage.removeItem('employerEPFNo'); // Remove legacy key as well
  }



  // Retrieve employerTypeId
  getEmployerTypeId(): number | null {
    const employerTypeId = sessionStorage.getItem('employerTypeId');
    return employerTypeId ? parseInt(employerTypeId, 10) : null;
  }

  // Clear employerTypeId
  clearEmployerTypeId(): void {
    sessionStorage.removeItem('employerTypeId');
  }

  // Store isPayRollEligible in sessionStorage
  storeIsPayRollEligible(value: boolean): void {
    sessionStorage.setItem('isPayRollEligible', value.toString());
  }

  // Retrieve isPayRollEligible
  getIsPayRollEligible(): boolean {
    return sessionStorage.getItem('isPayRollEligible') === 'true';
  }

  // Store isPayRollInvoiceEligible in sessionStorage
  storeIsPayRollInvoiceEligible(value: boolean): void {
    sessionStorage.setItem('isPayRollInvoiceEligible', value.toString());
  }

  // Retrieve isPayRollInvoiceEligible
  getIsPayRollInvoiceEligible(): boolean {
    return sessionStorage.getItem('isPayRollInvoiceEligible') === 'true';
  }
  // Retrieve all company details as an object
  getCompanyDetails(): any {
    return {
      companyName: sessionStorage.getItem('companyName'),
      firstName: sessionStorage.getItem('firstName'),
      lastName: sessionStorage.getItem('lastName'),
      email: sessionStorage.getItem('email'),
      lastLoginDate: sessionStorage.getItem('lastLoginDate'),
      companyAdminId: sessionStorage.getItem('companyAdminId'),
    };
  }

  // Clear all company details
  clearCompanyDetails(): void {

    sessionStorage.removeItem('companyName');
    sessionStorage.removeItem('firstName');
    sessionStorage.removeItem('lastName');
    sessionStorage.removeItem('email');
    sessionStorage.removeItem('lastLoginDate');
    sessionStorage.removeItem('employerTypeId');
    this.clearEmployerEPFNo(); // Also clear EPF number

  }

  logout() {
    sessionStorage.removeItem('token'); // Or clear entire storage
    this.navigationTracker.clearStoredRoute();
    this.router.navigate(['/login']); // redirect to login
    // Forcefully Clear all session storage
    sessionStorage.clear();
    // Forcefully Clear all local storage
    localStorage.clear();
  }

  // Check permissions and logout if invalid
  checkPermissionsOrLogout(): void {
    if (!this.authz.hasValidPermissionsInStorage()) {
      this.logout();
    }
  }

  // storePermissions(perms: LoginPermissionsDto | null | undefined) {
  //   this.authz.saveToStorage(perms);
  //   this.authz.load(perms);
  // }

  storePermissions(
    perms: LoginPermissionsDto | null | undefined,
    companyId: string | number,
    companyAdminId: string | number,
    employerTypeId: string | number
  ): void {
    // sessionStorage must already have companyId/companyAdminId/employerTypeId
    // before calling this
    this.authz.saveToStorage(perms, companyId, companyAdminId, employerTypeId);
    this.authz.load(perms);
  }

  /** call once at app start (e.g., in AppComponent or APP_INITIALIZER) */
  restorePermissions() {
    this.authz.restoreFromStorage();
  }

  //Update company details using PUT API
  updateCompanyDetails(companyData: any) {
    const token = this.getToken();
    const headers = { 'Authorization': `Bearer ${token}` };
    return this.http.put<{
      success: boolean;
      message: string;
      data: any;
    }>(`${this.gatewayBaseUrl}/Companies`, companyData, { headers });
  }

  //Get bank branches by bank ID
  getBankBranches(bankId: number) {
    const token = this.getToken();
    const headers = { 'Authorization': `Bearer ${token}` };
    return this.http.get<{
      success: boolean;
      message: string;
      data: any[];
    }>(`${this.gatewayBaseUrl}/Companies/bank-branches/${bankId}`, { headers });
  }

  //Get bank branches by bank code
  getBankBranchesByCode(bankCode: string) {
    const token = this.getToken();
    const headers = { 'Authorization': `Bearer ${token}` };
    return this.http.get<{
      success: boolean;
      message: string;
      data: any[];
    }>(`${this.gatewayBaseUrl}/Companies/bank-branches/${bankCode}`, { headers });
  }

  //Get banks (dropdown values)
  getBanks() {
    const token = this.getToken();
    const headers = { 'Authorization': `Bearer ${token}` };
    return this.http.get<{
      success: boolean;
      message: string;
      data: any[];
    }>(`${this.gatewayBaseUrl}/Companies/banks`, { headers });
  }

  //Get employee count categories
  getEmployeeCountCategories() {
    const token = this.getToken();
    const headers = { 'Authorization': `Bearer ${token}` };
    return this.http.get<{
      success: boolean;
      message: string;
      data: any[];
    }>(`${this.gatewayBaseUrl}/Companies/employee-count-categories`, { headers });
  }

  //Get industry types
  getIndustryTypes() {
    const token = this.getToken();
    const headers = { 'Authorization': `Bearer ${token}` };
    return this.http.get<{
      success: boolean;
      message: string;
      data: any[];
    }>(`${this.gatewayBaseUrl}/Companies/industry-types`, { headers });
  }

  //Get company legal types
  getCompanyLegalTypes() {
    const token = this.getToken();
    const headers = { 'Authorization': `Bearer ${token}` };
    return this.http.get<{
      success: boolean;
      message: string;
      data: LegalType[];
    }>(`${this.gatewayBaseUrl}/Companies/company-legal-types`, { headers });
  }

  //Get company details by company ID from API
  getCompanyDetailsFromAPI(companyId: string) {
    const token = this.getToken();
    const headers = { 'Authorization': `Bearer ${token}` };
    return this.http.get<{
      success: boolean;
      message: string;
      data: any;
    }>(`${this.gatewayBaseUrl}/Companies?CompanyId=${companyId}`, { headers });
  }

  // company profile API

  //Get company profile by company ID from API
  getCompanyProfile(companyId: string) {
    const token = this.getToken();
    const headers = { 'Authorization': `Bearer ${token}` };
    return this.http.get<{
      success: boolean;
      message: string;
      data: any;
    }>(`${this.gatewayBaseUrl}/Companies/${companyId}/profile`, { headers });
  }

  // ===== SECTION-WISE UPDATE APIs =====

  //Update company contact details
  updateCompanyContactDetails(companyId: string, contactData: any) {
    const token = this.getToken();
    const headers = { 'Authorization': `Bearer ${token}` };
    return this.http.post<{
      success: boolean;
      message: string;
      data: any;
    }>(`${this.gatewayBaseUrl}/Companies/${companyId}/contact-details`, contactData, { headers });
  }

  //Update company basic registration details
  updateCompanyBasicRegistration(companyId: string, basicData: any) {
    const token = this.getToken();
    const headers = { 'Authorization': `Bearer ${token}` };
    return this.http.post<{
      success: boolean;
      message: string;
      data: any;
    }>(`${this.gatewayBaseUrl}/Companies/${companyId}/basic-registration`, basicData, { headers });
  }

  //Update company financial snapshot
  updateCompanyFinancialSnapshot(companyId: string, financialData: any) {
    const token = this.getToken();
    const headers = { 'Authorization': `Bearer ${token}` };
    return this.http.post<{
      success: boolean;
      message: string;
      data: any;
    }>(`${this.gatewayBaseUrl}/Companies/${companyId}/financial-snapshot`, financialData, { headers });
  }

  //Update company authorized contact
  updateCompanyAuthorizedContact(companyId: string, contactData: any) {
    const token = this.getToken();
    const headers = { 'Authorization': `Bearer ${token}` };
    return this.http.post<{
      success: boolean;
      message: string;
      data: any;
    }>(`${this.gatewayBaseUrl}/Companies/${companyId}/authorized-contact`, contactData, { headers });
  }

  //Update company bank accounts
  updateCompanyBankAccounts(companyId: string, bankData: any) {
    const token = this.getToken();
    const headers = { 'Authorization': `Bearer ${token}` };
    return this.http.post<{
      success: boolean;
      message: string;
      data: any;
    }>(`${this.gatewayBaseUrl}/Companies/${companyId}/bank-accounts`, bankData, { headers });
  }

  //Update company secretary details
  updateCompanySecretary(companyId: string, secretaryData: any) {
    const token = this.getToken();
    const headers = { 'Authorization': `Bearer ${token}` };
    return this.http.post<{
      success: boolean;
      message: string;
      data: any;
    }>(`${this.gatewayBaseUrl}/Companies/${companyId}/secretary`, secretaryData, { headers });
  }

  //Update company auditor details
  updateCompanyAuditor(companyId: string, auditorData: any) {
    const token = this.getToken();
    const headers = { 'Authorization': `Bearer ${token}` };
    return this.http.post<{
      success: boolean;
      message: string;
      data: any;
    }>(`${this.gatewayBaseUrl}/Companies/${companyId}/auditor`, auditorData, { headers });
  }

  //Update company directors
  updateCompanyDirectors(companyId: string, directorsData: any) {
    const token = this.getToken();
    const headers = { 'Authorization': `Bearer ${token}` };
    return this.http.post<{
      success: boolean;
      message: string;
      data: any;
    }>(`${this.gatewayBaseUrl}/Companies/${companyId}/directors`, directorsData, { headers });
  }

  //Update company shareholders
  updateCompanyShareholders(companyId: string, shareholdersData: any) {
    const token = this.getToken();
    const headers = { 'Authorization': `Bearer ${token}` };
    return this.http.post<{
      success: boolean;
      message: string;
      data: any;
    }>(`${this.gatewayBaseUrl}/Companies/${companyId}/shareholders`, shareholdersData, { headers });
  }

  //Upload document file
  uploadDocumentFile(companyId: string, file: File, fileTitle: string) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('fileTitle', fileTitle);

    const token = this.getToken();
    const headers = { 'Authorization': `Bearer ${token}` };
    // Don't set Content-Type for FormData, let browser set it with boundary

    return this.http.post<{
      success: boolean;
      message: string;
      data: string; // The file URL is directly in the data field
    }>(`${this.gatewayBaseUrl}/Companies/${companyId}/profile-documents/upload`, formData, { headers });
  }

  //Update company documents
  updateCompanyDocuments(companyId: string, documentsData: any) {
    const token = this.getToken();
    const headers = { 'Authorization': `Bearer ${token}` };
    return this.http.post<{
      success: boolean;
      message: string;
      data: any;
    }>(`${this.gatewayBaseUrl}/Companies/${companyId}/documents`, documentsData, { headers });
  }

  //Get document view URL
  getDocumentViewUrl(companyId: string, fileName: string) {
    const token = this.getToken();
    const headers = { 'Authorization': `Bearer ${token}` };
    return this.http.get<Blob>(`${this.gatewayBaseUrl}/Companies/${companyId}/profile-documents/view?fileName=${fileName}`, {
      headers,
      responseType: 'blob' as 'json'
    });
  }

  //Get document template
  getDocumentTemplate(documentType: string) {
    const token = this.getToken();
    const headers = { 'Authorization': `Bearer ${token}` };
    return this.http.get<{
      success: boolean;
      message: string;
      data: any;
    }>(`${this.gatewayBaseUrl}/Companies/document-templates/${documentType}`, { headers });
  }

  //Download document template as Blob
  downloadDocumentTemplate(documentType: string): Observable<Blob> {
    const token = this.getToken();
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`
    });
    return this.http.get(`${this.gatewayBaseUrl}/Companies/document-templates/${documentType}`, {
      headers,
      responseType: 'blob' as 'json'
    }) as unknown as Observable<Blob>;
  }

  // forgot password APIs

  initiateForgotPassword(emailOrUserName: string): Observable<any> {
    return this.http.post(`${this.gatewayBaseUrl}/api/CompanyAdmins/forgot-password?emailOrUserName=${encodeURIComponent(emailOrUserName)}`, {});
  }

  verifyOtpReset(emailOrUserName: string, otp: string): Observable<any> {
    return this.http.post(`${this.gatewayBaseUrl}/api/CompanyAdmins/verify-forgot-password-otp?emailOrUserName=${encodeURIComponent(emailOrUserName)}&otp=${encodeURIComponent(otp)}`, {});
  }

  resetPassword(emailOrUserName: string, newPassword: string): Observable<any> {
    return this.http.post(`${this.gatewayBaseUrl}/api/CompanyAdmins/reset-password?emailOrUserName=${encodeURIComponent(emailOrUserName)}&newPassword=${encodeURIComponent(newPassword)}`, {});
  }

}
